import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  ISdkParams,
  ISdkSettings,
} from "@blings/blings-player/lib/src/SDK/sdk.api";
import classNames from "classnames";
import PlayerManager from "../utils/playerManager";
import { Player } from "@blings/blings-player";
import useDebounce from "../helpers/DebounceHook";
import { VideoCameraOutlined } from "@ant-design/icons";
import { Skeleton } from "antd";
import "./SdkPlayer.scss";
// Extend ISdkSettings to include startFrame
interface ExtendedSdkSettings extends ISdkSettings {
  startFrame?: number;
}

export type SdkPlayerProps = {
  sdkParams:
    | (Omit<ISdkParams, "settings"> & {
        settings: Omit<ExtendedSdkSettings, "container"> & {
          container?: HTMLElement;
        };
      })
    | null;
  outsideLoading?: (value: boolean) => void;
  style?: React.CSSProperties;
  className?: string;
  loadingClassName?: string;
  playerWrapperRef?: React.Ref<HTMLDivElement>;
  debounceMs?: number;
  onError?: (error: Error) => void;
  isFetchingData?: boolean;
};

const DEFAULT_DEBOUNCE_MS = 500;

export default function SdkPlayer(props: SdkPlayerProps) {
  const {
    sdkParams,
    style,
    className,
    playerWrapperRef,
    loadingClassName,
    outsideLoading,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    onError,
    isFetchingData = false,
  } = props;

  const debouncedSdkParams = useDebounce(sdkParams, debounceMs);
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const currentFrame = useRef<number>(0);
  const totalFrames = useRef<number>(0);
  const lastScene = useRef<string>("");
  const [isLoaderWrapperVisible, setIsLoaderWrapperVisible] =
    useState<boolean>(false);

  const setLoadingState = useCallback(
    (loading: boolean) => {
      setIsLoading(loading);
      outsideLoading?.(loading);
    },
    [outsideLoading]
  );

  const clearPlayerReference = useCallback(async () => {
    if (playerRef.current) {
      totalFrames.current = playerRef.current.animation.totalFrames;
      currentFrame.current = playerRef.current.animation.currentFrame;
      playerRef.current = null;
    }
  }, []);

  const createNewPlayer = async () => {
    if (!containerRef.current || !debouncedSdkParams) return;

    setLoadingState(true);
    setError(null);

    try {
      await clearPlayerReference();

      let startFrame = currentFrame.current;
      let posterFrame = currentFrame.current;
      const currentScene = JSON.stringify(debouncedSdkParams?.scenes?.[0]);

      if (
        totalFrames.current < currentFrame.current ||
        currentScene !== lastScene.current
      ) {
        startFrame = 0;
        posterFrame = debouncedSdkParams?.settings?.posterFrame || startFrame;
      }

      const fullSdkParams: ISdkParams = {
        ...debouncedSdkParams,
        settings: {
          analyticsConfig: { disable: true },
          ...debouncedSdkParams.settings,
          container: containerRef.current,
          startFrame,
          posterFrame,
        } as ExtendedSdkSettings,
      };

      playerRef.current = await PlayerManager.get().createPlayer(
        fullSdkParams,
        {
          newPlayer: true,
        }
      );
      lastScene.current = currentScene;
    } catch (e) {
      const error =
        e instanceof Error ? e : new Error("Failed to create player");
      console.error("Error creating player", error);
      setError(error);
      onError?.(error);
    } finally {
      setLoadingState(false);
    }
  };

  useEffect(() => {
    if (!isFetchingData) {
      createNewPlayer();
    }
    return () => {
      setLoadingState(false);
      clearPlayerReference();
    };
  }, [debouncedSdkParams, isFetchingData]);

  useEffect(() => {
    return () => {
      PlayerManager.get().deletePlayer();
      lastScene.current = "";
      currentFrame.current = 0;
      totalFrames.current = 0;
    };
  }, []);

  useEffect(() => {
    if (isLoading || isFetchingData || !debouncedSdkParams) {
      setIsLoaderWrapperVisible(true);
    } else {
      setIsLoaderWrapperVisible(false);
    }
  }, [isLoading, isFetchingData, debouncedSdkParams]);

  if (error) {
    return (
      <div
        className={classNames("vid-wrapper vid-error", className)}
        role="alert"
        aria-live="assertive"
      >
        <div className="vid-error-message">Failed to load player</div>
      </div>
    );
  }

  return (
    <div
      className={classNames(
        "vid-wrapper",
        isLoaderWrapperVisible && "empty-player " + loadingClassName,
        className
      )}
      style={style}
      ref={playerWrapperRef}
      role="region"
      aria-label="Video player"
      aria-busy={isLoading}
    >
      {isLoaderWrapperVisible && (
        <div
          className="loading-wrapper"
          role="status"
          aria-label={isFetchingData ? "Fetching data" : "Loading player"}
        >
          <Skeleton.Node active style={{ borderRadius: "50%" }}>
            <VideoCameraOutlined style={{ fontSize: 40, color: "#bfbfbf" }} />
          </Skeleton.Node>
          <span>{isFetchingData ? "Fetching data" : ""}</span>
        </div>
      )}

      <div
        className="vid-container"
        ref={containerRef}
        aria-hidden={isLoading}
      ></div>
    </div>
  );
}
